import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';

import type { CheckedCash } from '@/store/cash/api/checkCashByTradeCapture.ts';
import { isCashForward, isCashNdf, isCashSpot } from '@/store/cash/helpers/tile.ts';
import type { FxCash, FxCashForward, FxCashNdf, FxCashSpot } from '@/store/cash/state.ts';
import type { TradeCapture, TradeCaptureErrors, TradeCaptureWarnings } from '@/store/types.ts';
import type { TradeCaptureResponseWarnings } from '@/types/trade-capture.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import {
  getErrorPreviousNumberValue,
  getErrorPreviousStringDate,
  getNotional,
  getNotionalCurrencyIndex,
} from '@/utils/tradeCapture.ts';

/**
 * Business rules for updateCashFromTradeCapture:
 *
 * type:
 *   tradeCaptureCash.legs[0].productName defined ?
 *     => currentCash.type -> tradeCaptureCash.legs[0].productName
 *   else
 *     => currentCash.type (unchanged)
 *
 * properties (per type):
 *   currencyPair:
 *     tradeCaptureCash.changedFields.currencyPair defined ?
 *       => currentCash.properties.currencyPair -> tradeCaptureCash.changedFields.currencyPair
 *     else
 *       => currentCash.properties.currencyPair (unchanged)
 *
 *   currency1 / currency2:
 *     currencyPair split if defined, else keep previous values
 *
 *   notional:
 *     previous error value exists ?
 *       => previous error value
 *     else
 *       amount1 or amount2 defined ?
 *         => amount1 ?? amount2
 *       else
 *         => previous value
 *
 *   notionalCurrencyIndex:
 *     amount1String defined ?
 *       => 1
 *     amount2String defined ?
 *       => 2
 *     else
 *       => previous value
 *
 *   maturityDate:
 *     previous error value exists ?
 *       => previous error value
 *     else
 *       tradeCaptureCash.legs[0].maturityDate defined ?
 *         => tradeCaptureCash.legs[0].maturityDate
 *       else
 *         => previous value
 *
 *   maturityDateTenor:
 *     tradeCaptureCash.legs[0].maturityDateTenor defined ?
 *       => tradeCaptureCash.legs[0].maturityDateTenor
 *     else
 *       => previous value
 *     FxSpot: always 'SP'
 *
 *   isSpotWithInvertedCurrencyPair (Forward only):
 *     => isCurrencyPairInverted
 *
 *   NDF specific fields:
 *     field defined in tradeCaptureCash.legs[0] ?
 *       => previous value -> tradeCaptureCash.legs[0].field
 *     else
 *       => previous value
 *
 * tradeCapture:
 *   status: always 'CHECKED'
 *   sessionId: from parameter
 *   version: tradeCaptureCash.idVersion ?? 0
 *   isReadyToPrice, isPriceObsolete, isProductObsolete: from tradeCaptureCash.changedFields
 *   warnings/errors: mapped from tradeCaptureCash, resolved errors removed
 *
 * clientId:
 *   always kept from currentCash
 */

// ███████╗██████╗  ██████╗ ███╗   ███╗    ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
// ██╔════╝██╔══██╗██╔═══██╗████╗ ████║    ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
// █████╗  ██████╔╝██║   ██║██╔████╔██║       ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
// ██╔══╝  ██╔══██╗██║   ██║██║╚██╔╝██║       ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
// ██║     ██║  ██║╚██████╔╝██║ ╚═╝ ██║       ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═╝     ╚═╝       ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

/**
 * Updates a cash trade from trade capture response.
 * @param currentCash Current cash trade
 * @param isCurrencyPairInverted Whether the currency pair is inverted
 * @param sessionId Trade capture session ID
 * @param tradeCaptureCash Trade capture response
 * @returns Updated cash trade
 */
export const updateCashFromTradeCapture = (
  currentCash: FxCash,
  isCurrencyPairInverted: boolean,
  sessionId: string,
  tradeCaptureCash: CheckedCash,
): FxCash => {
  const tradeCaptureCashLeg = tradeCaptureCash.changedFields.legs['0'];

  if (isNotDefined(tradeCaptureCashLeg)) {
    return currentCash;
  }

  const tradeCaptureProductName = isDefined(tradeCaptureCashLeg.productName)
    ? tradeCaptureCashLeg.productName
    : currentCash.type;

  const tradeCapture = buildTradeCapture(sessionId, tradeCaptureCash);
  const clientId = currentCash.clientId;

  switch (tradeCaptureProductName) {
    case 'FxSpot':
      return {
        type: 'FxSpot',
        properties: updateSpotPropertiesFromTradeCapture(currentCash, tradeCaptureCash),
        tradeCapture,
        clientId,
      };

    case 'FxFwd':
      return {
        type: 'FxFwd',
        properties: updateForwardPropertiesFromTradeCapture(currentCash, isCurrencyPairInverted, tradeCaptureCash),
        tradeCapture,
        clientId,
      };

    case 'FxNdf':
      return {
        type: 'FxNdf',
        properties: updateNdfPropertiesFromTradeCapture(currentCash, tradeCaptureCash),
        tradeCapture,
        clientId,
      };

    default:
      return assertUnreachable(tradeCaptureProductName, `Unhandled cash product type: ${tradeCaptureProductName}`);
  }
};

// ███╗   ███╗ █████╗ ██████╗     ████████╗██████╗  █████╗ ██████╗ ███████╗ ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
// ████╗ ████║██╔══██╗██╔══██╗    ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
// ██╔████╔██║███████║██████╔╝       ██║   ██████╔╝███████║██║  ██║█████╗  ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
// ██║╚██╔╝██║██╔══██║██╔═══╝        ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝  ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
// ██║ ╚═╝ ██║██║  ██║██║            ██║   ██║  ██║██║  ██║██████╔╝███████╗╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
// ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝            ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝ ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

/**
 * Builds the trade capture object with errors and warnings.
 */
const buildTradeCapture = (sessionId: string, tradeCaptureCash: CheckedCash): TradeCapture<unknown> => {
  return {
    status: 'CHECKED',
    sessionId,
    version: tradeCaptureCash?.idVersion ?? 0,
    isReadyToPrice: tradeCaptureCash?.changedFields.isReadyToPrice ?? false,
    isPriceObsolete: tradeCaptureCash.changedFields?.isPriceObsolete ?? false,
    isProductObsolete: tradeCaptureCash.changedFields?.isProductObsolete ?? false,
    warnings: mapCashWarningsFromTradeCapture(tradeCaptureCash.warnings ?? undefined),
    errors: mapCashErrorsFromTradeCapture(tradeCaptureCash.errors ?? undefined),
  };
};

function mapCashWarningsFromTradeCapture(
  warnings: TradeCaptureResponseWarnings | undefined,
): TradeCaptureWarnings<FxCash['properties']> {
  const legWarnings = warnings?.legs?.[0];

  if (isNotDefined(legWarnings)) {
    return {};
  }

  return {
    notional: legWarnings.amount1 ?? legWarnings.amount2,
    maturityDate: legWarnings.maturityDate,
  };
}

function mapCashErrorsFromTradeCapture(
  errors: TradeCaptureResponseWarnings | undefined,
): TradeCaptureErrors<FxCash['properties']> {
  const legErrors = errors?.legs?.[0];

  if (isNotDefined(legErrors)) {
    return {};
  }

  return {
    notional: legErrors.amount1 ?? legErrors.amount2,
    maturityDate: legErrors.maturityDate,
  };
}

// ==== COMMON PROPERTIES

/**
 * Common properties shared by all cash types.
 */
type CommonCashProperties = {
  currencyPair: string | undefined;
  currency1: string | undefined;
  currency2: string | undefined;
  notional: number | undefined;
  notionalCurrencyIndex: CurrencyIndex | undefined;
  maturityDate: string | undefined;
  maturityDateTenor: string | undefined;
};

/**
 * Extracts and computes common properties from trade capture response.
 * This function handles all the shared logic between Spot, Forward and NDF.
 */
const getCommonUpdatedProperties = <T extends FxCash['properties']>(
  tradeCaptureCash: CheckedCash,
  previousProperties: T,
): Partial<CommonCashProperties> => {
  const tradeCaptureCashFields = tradeCaptureCash.changedFields;
  const tradeCaptureCashLeg = tradeCaptureCashFields.legs?.['0'];

  if (isNotDefined(tradeCaptureCashFields) || isNotDefined(tradeCaptureCashLeg)) {
    return {};
  }

  const [currency1, currency2] = tradeCaptureCashFields.currencyPair?.split('/') ?? [];

  const notional = getNotional(
    tradeCaptureCashLeg.amount1,
    tradeCaptureCashLeg.amount2,
    previousProperties.notional,
    getErrorPreviousNumberValue('amount1', tradeCaptureCash.errors) ??
      getErrorPreviousNumberValue('amount2', tradeCaptureCash.errors),
  );

  const notionalCurrencyIndex = getNotionalCurrencyIndex(
    tradeCaptureCashLeg.amount1,
    tradeCaptureCashLeg.amount2,
    previousProperties.notionalCurrencyIndex,
  );

  const maturityDate =
    getErrorPreviousStringDate('maturityDate', tradeCaptureCash.errors) ??
    (tradeCaptureCashLeg.maturityDate !== undefined
      ? (tradeCaptureCashLeg.maturityDate ?? undefined)
      : previousProperties.maturityDate);

  const maturityDateTenor =
    tradeCaptureCashLeg.maturityDateTenor !== undefined
      ? (tradeCaptureCashLeg.maturityDateTenor ?? undefined)
      : previousProperties.maturityDateTenor;

  return {
    currencyPair: tradeCaptureCashFields.currencyPair ?? previousProperties.currencyPair,
    currency1: currency1 ?? previousProperties.currency1,
    currency2: currency2 ?? previousProperties.currency2,
    notional,
    notionalCurrencyIndex,
    maturityDate,
    maturityDateTenor,
  };
};

// ==== SPOT

/**
 * Updates Spot properties from trade capture.
 */
const updateSpotPropertiesFromTradeCapture = (
  currentCash: FxCash,
  tradeCaptureCash: CheckedCash,
): FxCashSpot['properties'] => {
  const previousProperties = getPreviousSpotProperties(currentCash);
  const commonProperties = getCommonUpdatedProperties(tradeCaptureCash, previousProperties);

  return {
    ...previousProperties,
    ...commonProperties,
    maturityDateTenor: 'SP',
    // reset any previous error
    error: undefined,
  } satisfies FxCashSpot['properties'];
};

const getPreviousSpotProperties = (currentCash: FxCash): FxCashSpot['properties'] => {
  return isCashSpot(currentCash)
    ? currentCash.properties
    : {
        currencyPair: currentCash.properties.currencyPair,
        currency1: currentCash.properties.currency1,
        currency2: currentCash.properties.currency2,
        notional: currentCash.properties.notional,
        notionalCurrencyIndex: currentCash.properties.notionalCurrencyIndex,
        margin: undefined,
      };
};

// ==== FORWARD

/**
 * Updates Forward properties from trade capture.
 */
const updateForwardPropertiesFromTradeCapture = (
  currentCash: FxCash,
  isSpotWithInvertedCurrencyPair: boolean,
  tradeCaptureCash: CheckedCash,
): FxCashForward['properties'] => {
  const previousProperties = getPreviousForwardProperties(currentCash);
  const commonProperties = getCommonUpdatedProperties(tradeCaptureCash, previousProperties);

  return {
    ...previousProperties,
    ...commonProperties,
    isSpotWithInvertedCurrencyPair,
    // reset any previous error
    error: undefined,
  } satisfies FxCashForward['properties'];
};

const getPreviousForwardProperties = (currentCash: FxCash): FxCashForward['properties'] => {
  return isCashForward(currentCash)
    ? currentCash.properties
    : {
        currencyPair: currentCash.properties.currencyPair,
        currency1: currentCash.properties.currency1,
        currency2: currentCash.properties.currency2,
        notional: currentCash.properties.notional,
        notionalCurrencyIndex: currentCash.properties.notionalCurrencyIndex,
        maturityDate: currentCash.properties.maturityDate,
        maturityDateTenor: currentCash.properties.maturityDateTenor,
        isSpotWithInvertedCurrencyPair: false,
        rfs: currentCash.properties.rfs,
      };
};

// ==== NDF

/**
 * Updates NDF properties from trade capture.
 */
const updateNdfPropertiesFromTradeCapture = (
  currentCash: FxCash,
  tradeCaptureCash: CheckedCash,
): FxCashNdf['properties'] => {
  const previousProperties = getPreviousNdfProperties(currentCash);
  const commonProperties = getCommonUpdatedProperties(tradeCaptureCash, previousProperties);
  const ndfSpecificProperties = getNdfSpecificProperties(tradeCaptureCash, previousProperties);

  return {
    ...previousProperties,
    ...commonProperties,
    ...ndfSpecificProperties,
    // reset any previous error
    error: undefined,
  } satisfies FxCashNdf['properties'];
};

const getPreviousNdfProperties = (currentCash: FxCash): FxCashNdf['properties'] => {
  if (isCashNdf(currentCash)) {
    return currentCash.properties;
  }

  const { currencyPair, currency1, currency2, notional, notionalCurrencyIndex, maturityDate, maturityDateTenor, rfs } =
    currentCash.properties;

  return {
    currencyPair,
    currency1,
    currency2,
    notional,
    notionalCurrencyIndex,
    maturityDate,
    maturityDateTenor,
    rfs,
    margin: undefined,
    fixingDate: undefined,
    fixingSource: undefined,
    fixingCurrency: undefined,
    possibleFixingCurrencies: undefined,
    possibleFixingSources: undefined,
    isCrossed: undefined,
    possibleXCurrencies: undefined,
    possibleSndFixingSources: undefined,
    xCurrency: undefined,
    sndFixingSource: undefined,
    fixingSourceOverriddenDefault: undefined,
    sndFixingSourceOverriddenDefault: undefined,
  };
};

/**
 * Extracts NDF-specific properties from trade capture response.
 */
const getNdfSpecificProperties = (
  tradeCaptureCash: CheckedCash,
  previousNdfProperties: FxCashNdf['properties'],
): Partial<FxCashNdf['properties']> => {
  const tradeCaptureCashLeg = tradeCaptureCash.changedFields.legs?.['0'];

  if (isNotDefined(tradeCaptureCashLeg)) {
    return {};
  }

  return {
    rfs: previousNdfProperties.rfs,
    margin: previousNdfProperties.margin,
    fixingDate: tradeCaptureCashLeg.fixingDate ?? previousNdfProperties.fixingDate,
    fixingSource:
      tradeCaptureCashLeg.fixingSource ??
      previousNdfProperties.fixingSource ??
      tradeCaptureCashLeg?.possibleFixingSources?.default ??
      previousNdfProperties?.possibleFixingSources?.default,
    fixingCurrency: tradeCaptureCashLeg.fixingCurrency ?? previousNdfProperties.fixingCurrency,
    possibleFixingCurrencies:
      tradeCaptureCashLeg.possibleFixingCurrencies ?? previousNdfProperties.possibleFixingCurrencies,
    possibleFixingSources: tradeCaptureCashLeg.possibleFixingSources ?? previousNdfProperties.possibleFixingSources,
    isCrossed: tradeCaptureCashLeg.isCrossed ?? previousNdfProperties.isCrossed ?? false,
    possibleXCurrencies: tradeCaptureCashLeg.possibleXCurrencies ?? previousNdfProperties.possibleXCurrencies,
    possibleSndFixingSources:
      tradeCaptureCashLeg.possibleSndFixingSources ?? previousNdfProperties.possibleSndFixingSources,
    xCurrency: tradeCaptureCashLeg.xCurrency ?? previousNdfProperties.xCurrency,
    sndFixingSource: tradeCaptureCashLeg.sndFixingSource ?? previousNdfProperties.sndFixingSource,
    fixingSourceOverriddenDefault: previousNdfProperties.fixingSourceOverriddenDefault,
    sndFixingSourceOverriddenDefault: previousNdfProperties.sndFixingSourceOverriddenDefault,
  };
};
